import {Checkbox, FormControlLabel, Grid, MenuItem, Radio} from '@mui/material'
import {useEffect} from 'react'
import {Controller, useForm} from 'react-hook-form'
import PropTypes from 'prop-types'
import {StyledAsterisk} from '../../../../Common/Styles'
import {
  StyledBox,
  StyledLabel,
  StyledFormControl,
  StyledTextField,
  StyledSelect,
  StyledRadioGroup,
  StyledErrorBox,
  StyledErrorInputLabel,
  StyledTopGrid,
  StyledBottomGrid,
  StyledBoxContainer
} from './Styles'
import paginationLimit from '../../../../../configs/paginationLimit.json'

const fileTypeOptions = [
  {value: 'csv', label: 'CSV'},
  {value: 'xlsx', label: 'XLSX'}
]

export const columnNameOptions = [
  {value: 'first_name', label: 'First name'},
  {value: 'last_name', label: 'Last name'},
  {value: 'email', label: 'Email address'}
]

const ColumnCheckboxGroup = ({value, onChange}) => {
  const handleCheckboxChange = (e, optionValue) => {
    const newValue = e.target.checked ? [...(value || []), optionValue] : (value || []).filter((v) => v !== optionValue)
    onChange(newValue)
  }

  return (
    <StyledBoxContainer>
      {columnNameOptions.map((option) => (
        <FormControlLabel
          key={option.value}
          control={
            <Checkbox
              checked={value?.includes(option.value) || false}
              onChange={(e) => handleCheckboxChange(e, option.value)}
            />
          }
          label={<span style={{color: 'black'}}>{option.label}</span>}
        />
      ))}
    </StyledBoxContainer>
  )
}

const ExportListDialog = ({onFormDataChange, fileType, constituentsList, documentName, count}) => {
  const {
    control,
    watch,
    clearErrors,
    formState: {errors}
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      fileType: fileType || 'csv',
      documentName,
      limit: 'all',
      pageRange: '',
      selectedColumns: columnNameOptions.map((option) => option.value)
    }
  })

  const formValues = watch()
  const limitType = watch('limit')
  const isConstituentsListPresent = constituentsList && constituentsList.length > 0
  const recordsPerPage = paginationLimit.pagination.prospectsSegment
  const totalPages = count ? Math.ceil(count / recordsPerPage) : 0

  useEffect(() => {
    if (limitType === 'all' || isConstituentsListPresent) {
      clearErrors('pageRange')
    }
  }, [limitType, clearErrors, isConstituentsListPresent])

  useEffect(() => {
    const documentName = formValues.documentName?.trim() || ''
    const hasSpecialChars = /[^A-Za-z0-9 .,:_&@-]/.test(documentName)
    const hasAlphanumeric = /[A-Za-z0-9]/.test(documentName)
    const isPageRangeValid =
      isConstituentsListPresent ||
      formValues.limit === 'all' ||
      (formValues.limit === 'pages' && formValues.pageRange?.trim())
    const hasSelectedColumns = formValues.selectedColumns && formValues.selectedColumns.length > 0
    const isFormValid =
      formValues.fileType &&
      documentName.length <= 100 &&
      documentName.trim().length > 0 &&
      !hasSpecialChars &&
      hasAlphanumeric &&
      (isConstituentsListPresent || formValues.limit) &&
      isPageRangeValid &&
      hasSelectedColumns

    if (onFormDataChange) {
      onFormDataChange({
        fileType: formValues.fileType,
        documentName,
        limit: formValues.limit,
        pageRange: formValues.pageRange,
        selectedColumns: formValues.selectedColumns || [],
        isValid: isFormValid
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, onFormDataChange])

  return (
    <>
      <StyledTopGrid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledBox>
            <StyledLabel>
              Export type
              <StyledAsterisk sx={{marginLeft: '4px'}}>*</StyledAsterisk>
            </StyledLabel>
            <Controller
              name="fileType"
              control={control}
              rules={{required: 'File type is required'}}
              render={({field: {onChange, value}}) => (
                <StyledFormControl fullWidth error={!!errors.fileType} size="small" required>
                  <StyledSelect value={value} onChange={onChange}>
                    {fileTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </StyledFormControl>
              )}
            />
            {errors.fileType && (
              <StyledErrorBox>
                <StyledErrorInputLabel>{errors.fileType.message}</StyledErrorInputLabel>
              </StyledErrorBox>
            )}
          </StyledBox>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StyledBox>
            <StyledLabel>
              Document name
              <StyledAsterisk sx={{marginLeft: '4px'}}>*</StyledAsterisk>
            </StyledLabel>
            <Controller
              name="documentName"
              control={control}
              rules={{
                required: 'Document name is required',
                maxLength: {
                  value: 100,
                  message: 'Document name cannot be more than 100 characters'
                },
                validate: {
                  hasAlphanumeric: (value) =>
                    /[A-Za-z0-9]/.test(value) || 'Document name must contain at least one alphanumeric character',
                  noInvalidChars: (value) =>
                    !/[^A-Za-z0-9 .,:_&@-]/.test(value) || 'Document name cannot contain special characters'
                }
              }}
              render={({field: {onChange, value}}) => (
                <StyledTextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  required
                  value={value}
                  onChange={onChange}
                  error={!!errors.documentName}
                  helperText={errors.documentName?.message}
                  placeholder="Enter document name"
                />
              )}
            />
          </StyledBox>
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <StyledBox>
            <StyledLabel>
              Table page range
              {!isConstituentsListPresent && <StyledAsterisk sx={{marginLeft: '4px'}}>*</StyledAsterisk>}
            </StyledLabel>
            <Controller
              name="limit"
              control={control}
              rules={{required: isConstituentsListPresent ? false : 'Number of records is required'}}
              render={({field: {onChange, value}}) => (
                <StyledRadioGroup value={value} onChange={onChange}>
                  <FormControlLabel
                    value={'all'}
                    control={<Radio />}
                    label={<span style={{color: 'black'}}>All</span>}
                    disabled={isConstituentsListPresent}
                  />
                  <FormControlLabel
                    value={'pages'}
                    control={<Radio />}
                    disabled={isConstituentsListPresent}
                    label={
                      <StyledBoxContainer>
                        <span style={{color: 'black'}}>Range</span>
                        <Controller
                          name="pageRange"
                          control={control}
                          rules={{
                            required:
                              limitType === 'pages' && !isConstituentsListPresent ? 'Page range is required' : false,
                            validate: (value) => {
                              if (isConstituentsListPresent || limitType !== 'pages') return true
                              if (!value?.trim()) return 'Page range is required'
                              const trimmedValue = value.trim()
                              if (!/^\d+(-\d+)?$/.test(trimmedValue))
                                return 'Invalid page range format. Use format like 1-3'

                              const [start, end] = trimmedValue.split('-').map(Number)
                              // Check if range has two numbers and validate start < end
                              if (trimmedValue.includes('-')) {
                                if (start > end) return 'Start page must be less than end page'
                              }
                              if (start === 0 || end === 0) return 'Page numbers must be greater than 0'

                              // Validate against available records
                              if (totalPages > 0) {
                                const endPage = end || start
                                if (start > totalPages) {
                                  return `Range start ${start} exceeds total pages ${totalPages}`
                                }
                                if (endPage > totalPages) {
                                  return `Range end ${endPage} exceeds total pages ${totalPages}`
                                }
                              }

                              return true
                            }
                          }}
                          render={({field: {onChange: onPageChange, value: pageValue}}) => (
                            <StyledTextField
                              size="small"
                              variant="outlined"
                              value={pageValue}
                              onChange={onPageChange}
                              placeholder="1-3"
                              error={!!errors.pageRange}
                              disabled={isConstituentsListPresent || limitType !== 'pages'}
                              onClick={(e) => e.stopPropagation()}
                              sx={{width: '100px', marginLeft: '8px'}}
                            />
                          )}
                        />
                      </StyledBoxContainer>
                    }
                  />
                </StyledRadioGroup>
              )}
            />
            {(errors.limit || errors.pageRange) && (
              <StyledErrorBox>
                <StyledErrorInputLabel>{errors.limit?.message || errors.pageRange?.message}</StyledErrorInputLabel>
              </StyledErrorBox>
            )}
          </StyledBox>
        </Grid>
      </StyledTopGrid>
      <StyledBottomGrid container spacing={3}>
        <Grid item xs={12}>
          <StyledBox sx={{gap: 0}}>
            <StyledLabel>
              Columns type included
              <StyledAsterisk sx={{marginLeft: '4px'}}>*</StyledAsterisk>
            </StyledLabel>
            <Controller
              name="selectedColumns"
              control={control}
              rules={{
                validate: (value) => (value && value.length > 0) || 'At least one column must be selected'
              }}
              render={({field: {onChange, value}}) => <ColumnCheckboxGroup value={value} onChange={onChange} />}
            />
            {errors.selectedColumns && (
              <StyledErrorBox>
                <StyledErrorInputLabel>{errors.selectedColumns.message}</StyledErrorInputLabel>
              </StyledErrorBox>
            )}
          </StyledBox>
        </Grid>
      </StyledBottomGrid>
    </>
  )
}

ExportListDialog.propTypes = {
  onFormDataChange: PropTypes.func,
  fileType: PropTypes.string,
  constituentsList: PropTypes.array.isRequired,
  documentName: PropTypes.string.isRequired,
  count: PropTypes.number
}

ColumnCheckboxGroup.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

export default ExportListDialog
